import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import deleteDocumentId from "../http/deleteDocumentId";
import getDocumentIds from "../http/getDocumentIds";
import getNextDocumentNumber from "../http/getNextDocumentNumber";
import patchDocumentId from "../http/patchDocumentId";
import postDocumentId, {
  CreateDocumentIdRequest,
} from "../http/postDocumentId";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

interface UseDocumentIdsParams {
  type: string;
  startDate: string;
  endDate: string;
}

const useDocumentIds = ({ type, startDate, endDate }: UseDocumentIdsParams) => {
  const queryClient = useQueryClient();
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  const fetchDocumentIds = useQuery(
    ["document-ids", type, startDate, endDate],
    async () =>
      getDocumentIds(
        await getAuthTokenBlocking(getToken, relogin),
        type,
        startDate,
        endDate
      ),
    {
      enabled: !!type,
    }
  );

  const fetchNextNumber = useQuery(
    ["document-ids-next", type],
    async () =>
      getNextDocumentNumber(
        await getAuthTokenBlocking(getToken, relogin),
        type
      ),
    {
      enabled: !!type,
    }
  );

  const createDocumentId = useMutation(
    async (body: CreateDocumentIdRequest) =>
      postDocumentId(await getAuthTokenBlocking(getToken, relogin), body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["document-ids"]);
        queryClient.invalidateQueries(["document-ids-next"]);
      },
    }
  );

  const removeDocumentId = useMutation(
    async ({ type, number }: { type: string; number: number }) =>
      deleteDocumentId(await getAuthTokenBlocking(getToken, relogin), type, number),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["document-ids"]);
        queryClient.invalidateQueries(["document-ids-next"]);
      },
    }
  );

  const voidDocumentId = useMutation(
    async ({ type, number, isVoided }: { type: string; number: number; isVoided: boolean }) =>
      patchDocumentId(await getAuthTokenBlocking(getToken, relogin), type, number, { isVoided }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["document-ids"]);
      },
    }
  );

  return {
    fetchDocumentIds,
    fetchNextNumber,
    createDocumentId,
    removeDocumentId,
    voidDocumentId,
  };
};

export default useDocumentIds;
